import { useState, useEffect, useCallback } from 'react';
import { getPosition } from '../../lib/geo';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Button } from '../../components/ui/button';
import {
    Loader2,
    Sunrise,
    Sun,
    CloudSun,
    Sunset,
    Moon,
    MapPin,
    Clock,
    AlertCircle,
    RefreshCw,
} from 'lucide-react';

// Coordonnées de repli : Dakar (Sénégal).
const DAKAR = { lat: 14.6928, lon: -17.4467, label: 'Dakar' };

// Définition des 5 prières quotidiennes (ordre chronologique).
// `key` correspond à la clé renvoyée par l'API Aladhan.
const PRAYERS = [
    { key: 'Fajr', name: 'Fajr', wolof: 'Soubh', icon: Sunrise },
    { key: 'Dhuhr', name: 'Dhuhr', wolof: 'Tisbar', icon: Sun },
    { key: 'Asr', name: 'Asr', wolof: 'Takkousan', icon: CloudSun },
    { key: 'Maghrib', name: 'Maghrib', wolof: 'Timis', icon: Sunset },
    { key: 'Isha', name: 'Isha', wolof: 'Guéwé', icon: Moon },
];

/**
 * Convertit une heure "HH:MM" du jour courant en objet Date.
 */
const toTodayDate = (hhmm) => {
    const [h, m] = hhmm.split(':').map(Number);
    const d = new Date();
    d.setHours(h, m, 0, 0);
    return d;
};

/**
 * Formate une durée (en millisecondes) en "Xh Ymin".
 */
const formatCountdown = (ms) => {
    if (ms <= 0) return '0min';
    const totalMinutes = Math.floor(ms / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours > 0) return `${hours}h ${String(minutes).padStart(2, '0')}min`;
    return `${minutes}min`;
};

export const PrayerTimes = () => {
    const [timings, setTimings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [location, setLocation] = useState(null);
    const [now, setNow] = useState(new Date());

    // Récupère les horaires de prière depuis l'API Aladhan.
    const fetchTimings = useCallback(async (lat, lon, label) => {
        setLoading(true);
        setError(null);
        try {
            const timestampSec = Math.floor(Date.now() / 1000);
            const url = `https://api.aladhan.com/v1/timings/${timestampSec}?latitude=${lat}&longitude=${lon}&method=2`;
            const res = await fetch(url);
            if (!res.ok) throw new Error('Réponse invalide de l\'API');
            const data = await res.json();
            if (!data?.data?.timings) throw new Error('Données indisponibles');
            setTimings(data.data.timings);
            setLocation(label);
        } catch (err) {
            setError("Impossible de récupérer les horaires de prière. Vérifiez votre connexion.");
        } finally {
            setLoading(false);
        }
    }, []);

    // Au montage : tente la géolocalisation (permission native sur mobile),
    // sinon repli sur Dakar.
    const init = useCallback(() => {
        getPosition()
            .then(({ lat, lon }) => fetchTimings(lat, lon, 'Votre position'))
            .catch(() => fetchTimings(DAKAR.lat, DAKAR.lon, DAKAR.label));
    }, [fetchTimings]);

    useEffect(() => {
        init();
    }, [init]);

    // Tic d'horloge chaque seconde (horloge en direct + compte à rebours).
    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(id);
    }, []);

    // Détermine la prochaine prière et le temps restant.
    const getNextPrayer = () => {
        if (!timings) return null;
        for (const prayer of PRAYERS) {
            const time = timings[prayer.key];
            if (!time) continue;
            const prayerDate = toTodayDate(time);
            if (prayerDate > now) {
                return { ...prayer, time, diff: prayerDate - now };
            }
        }
        // Toutes les prières du jour sont passées : la prochaine est le Fajr de demain.
        const fajr = timings.Fajr;
        if (fajr) {
            const fajrTomorrow = toTodayDate(fajr);
            fajrTomorrow.setDate(fajrTomorrow.getDate() + 1);
            return { ...PRAYERS[0], time: fajr, diff: fajrTomorrow - now };
        }
        return null;
    };

    const nextPrayer = getNextPrayer();

    // Progression dans la fenêtre prière précédente → prochaine prière.
    let progressPct = 0;
    if (timings && nextPrayer) {
        const passed = PRAYERS
            .map((p) => ({ p, d: toTodayDate(timings[p.key]) }))
            .filter((x) => x.d <= now);
        let prevDate;
        if (passed.length) {
            prevDate = passed[passed.length - 1].d;
        } else if (timings.Isha) {
            prevDate = toTodayDate(timings.Isha);
            prevDate.setDate(prevDate.getDate() - 1); // Isha de la veille
        }
        if (prevDate) {
            const nextDate = new Date(now.getTime() + nextPrayer.diff);
            const total = nextDate - prevDate;
            progressPct = total > 0 ? Math.min(100, Math.max(0, ((now - prevDate) / total) * 100)) : 0;
        }
    }

    // Heure courante "HH:MM".
    const currentTime = now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

    return (
        <Card className="overflow-hidden rounded-3xl border-0 shadow-lg bg-gradient-to-br from-indigo-600 via-indigo-500 to-emerald-500 text-white">
            <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 text-xl font-bold">
                        <Clock className="h-5 w-5" />
                        Horaires des prières
                    </CardTitle>
                    {location && (
                        <Badge className="bg-white/20 text-white hover:bg-white/30 border-0 gap-1">
                            <MapPin className="h-3 w-3" />
                            {location}
                        </Badge>
                    )}
                </div>
            </CardHeader>

            <CardContent>
                {loading && (
                    <div className="flex flex-col items-center justify-center py-12 gap-3">
                        <Loader2 className="h-8 w-8 animate-spin" />
                        <p className="text-white/80 text-sm">Calcul des horaires...</p>
                    </div>
                )}

                {!loading && error && (
                    <Alert className="bg-white/10 border-white/20 text-white">
                        <AlertCircle className="h-4 w-4 !text-white" />
                        <AlertDescription className="flex flex-col gap-3 text-white/90">
                            {error}
                            <Button
                                onClick={init}
                                variant="secondary"
                                size="sm"
                                className="w-fit gap-2 bg-white/20 hover:bg-white/30 text-white border-0"
                            >
                                <RefreshCw className="h-3.5 w-3.5" />
                                Réessayer
                            </Button>
                        </AlertDescription>
                    </Alert>
                )}

                {!loading && !error && timings && (
                    <div className="space-y-4">
                        {/* Mise en évidence de la prochaine prière */}
                        {nextPrayer && (
                            <div className="rounded-2xl bg-white/15 backdrop-blur-sm p-4 text-center">
                                <div className="flex items-center justify-between text-xs uppercase tracking-wide text-white/70">
                                    <span>Prochaine prière</span>
                                    <span className="flex items-center gap-1 tabular-nums normal-case"><Clock className="h-3 w-3" />{currentTime}</span>
                                </div>
                                <p className="text-2xl font-bold mt-1">
                                    {nextPrayer.name}{' '}
                                    <span className="text-white/80 text-lg font-medium">
                                        ({nextPrayer.wolof})
                                    </span>
                                </p>
                                <p className="text-white/90 mt-1">
                                    à {nextPrayer.time} · dans{' '}
                                    <span className="font-semibold">
                                        {formatCountdown(nextPrayer.diff)}
                                    </span>
                                </p>
                                {/* Barre de progression vers la prochaine prière */}
                                <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-white/20">
                                    <div className="h-full rounded-full bg-white transition-all duration-1000 ease-linear" style={{ width: `${progressPct}%` }} />
                                </div>
                            </div>
                        )}

                        {/* Liste des 5 prières */}
                        <ul className="space-y-2">
                            {PRAYERS.map((prayer) => {
                                const Icon = prayer.icon;
                                const isNext = nextPrayer && nextPrayer.key === prayer.key;
                                return (
                                    <li
                                        key={prayer.key}
                                        className={`flex items-center justify-between rounded-2xl px-4 py-3 transition-colors ${
                                            isNext
                                                ? 'bg-white text-indigo-700 shadow-md font-semibold'
                                                : 'bg-white/10'
                                        }`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <Icon
                                                className={`h-5 w-5 ${
                                                    isNext ? 'text-indigo-600' : 'text-white/90'
                                                }`}
                                            />
                                            <div className="leading-tight">
                                                <p className="font-semibold">{prayer.name}</p>
                                                <p
                                                    className={`text-xs ${
                                                        isNext ? 'text-indigo-500' : 'text-white/60'
                                                    }`}
                                                >
                                                    {prayer.wolof}
                                                </p>
                                            </div>
                                        </div>
                                        <span className="text-lg font-bold tabular-nums">
                                            {timings[prayer.key]}
                                        </span>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};
